import * as React from 'react';
import { Plus, Trash2, Box } from 'lucide-react';
import { cn } from '../lib/utils.js';
import type { App } from '../lib/api.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.js';

export interface AppManagerProps {
  /** Array of apps to display */
  apps: App[];
  /** Callback when a new app is created */
  onCreateApp: (name: string) => Promise<void>;
  /** Callback when an app is deleted */
  onDeleteApp: (appId: string) => Promise<void>;
  /** Callback when an app is selected */
  onSelectApp?: (app: App) => void;
  /** Currently selected app ID */
  selectedAppId?: string | null;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Additional class name */
  className?: string;
}

/**
 * AppManager provides an interface for managing apps.
 */
export function AppManager({
  apps,
  onCreateApp,
  onDeleteApp,
  onSelectApp,
  selectedAppId,
  isLoading = false,
  error = null,
  className,
}: AppManagerProps) {
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [newAppName, setNewAppName] = React.useState('');
  const [isCreating, setIsCreating] = React.useState(false);
  const [deleteAppId, setDeleteAppId] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  const appToDelete = apps.find((app) => app.id === deleteAppId);

  const openCreateDialog = () => {
    setNewAppName('');
    setCreateError(null);
    setShowCreateDialog(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);

    if (!newAppName.trim()) {
      setCreateError('App name is required');
      return;
    }

    setIsCreating(true);
    try {
      await onCreateApp(newAppName.trim());
      setNewAppName('');
      setShowCreateDialog(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create app');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteAppId) return;

    setIsDeleting(true);
    try {
      await onDeleteApp(deleteAppId);
      setDeleteAppId(null);
    } catch {
      // Error should be handled by parent
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="border-b border-[var(--color-border)] bg-[var(--color-secondary)]/30">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-primary)]/10">
            <Box className="h-5 w-5 text-[var(--color-primary)]" />
          </div>
          <div>
            <CardTitle className="text-[var(--color-foreground)]">Apps</CardTitle>
            <CardDescription className="text-[var(--color-muted-foreground)]">
              Manage your applications. Each app has its own isolated log database.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-6">
        {/* Create App Button */}
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4" />
          Create App
        </Button>

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
            {error}
          </div>
        )}

        {/* Apps Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3 text-[var(--color-muted-foreground)]">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-primary)] border-t-transparent" />
              <span className="text-sm">Loading apps...</span>
            </div>
          </div>
        ) : apps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-secondary)]">
              <Box className="h-6 w-6 text-[var(--color-muted-foreground)]" />
            </div>
            <p className="mt-4 text-[var(--color-muted-foreground)]">
              No apps yet. Create your first app above.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map((app) => (
                  <TableRow
                    key={app.id}
                    className={cn(
                      onSelectApp && 'cursor-pointer',
                      selectedAppId === app.id && 'bg-[var(--color-primary)]/5'
                    )}
                    onClick={() => onSelectApp?.(app)}
                  >
                    <TableCell className="font-medium text-[var(--color-foreground)]">
                      {app.name}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-[var(--color-muted-foreground)]">
                      {app.id}
                    </TableCell>
                    <TableCell className="text-sm text-[var(--color-muted-foreground)]">
                      {formatDate(app.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteAppId(app.id);
                        }}
                        title="Delete app"
                        className="text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Create App Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => !open && setShowCreateDialog(false)}>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Create App</DialogTitle>
                <DialogDescription>
                  Create a new application. Each app has its own isolated log database and API keys.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[var(--color-foreground)]">
                    App Name
                  </label>
                  <Input
                    type="text"
                    value={newAppName}
                    onChange={(e) => setNewAppName(e.target.value)}
                    placeholder="my-application"
                    disabled={isCreating}
                    className="w-full"
                    autoFocus
                  />
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    A unique name for your application (e.g., web-app, api-server).
                  </p>
                </div>
                {createError && (
                  <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
                    {createError}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreating || !newAppName.trim()}>
                  {isCreating ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Create App
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteAppId} onOpenChange={(open) => !open && setDeleteAppId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete App</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{appToDelete?.name}</strong>?
                This will permanently delete all logs and API keys associated with this app.
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteAppId(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default AppManager;
